import React, { useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";

import {
  fetchProcedureCatalog,
  getCachedProcedureCatalog,
} from "./ProcedureCatalogAllService";

/**
 * Fuzzy-search dropdown for the procedure (CPT) catalog. Catalog-only picker
 * — free-text entries are not accepted, so the persisted `code` field is
 * always meaningful.
 *
 * Data source: full catalog loaded once via ProcedureCatalogAllService, then
 * cached module-level for the rest of the session. All filtering happens
 * locally — no per-keystroke round trip, no debounce.
 *
 * Match rules: case-insensitive substring on BOTH `name` and `code`, so a
 * clinician can type "43775" or "sleeve" and land the same row. Results are
 * capped at 40 rows per keystroke — enough to make the dropdown feel
 * discoverable without dumping the whole catalog into the DOM.
 *
 * Already-picked rows (matched by `pickedSet`) render greyed out and are
 * click-inert — same dedup UX as the medicine picker.
 */
const MAX_RESULTS = 40;

const identityKey = (r) =>
  ((r && r.code) || (r && r.name) || "").toString().trim().toLowerCase();

const ProcedureCatalogAutocomplete = ({ onAdd, disabled, placeholder, pickedSet }) => {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  // Seeded from the module-level cache so a picker opened AFTER the first
  // load renders instantly; the mount-time fetch below refreshes if the
  // cache was empty when we mounted.
  const [catalog, setCatalog] = useState(() => getCachedProcedureCatalog() || []);
  const [loading, setLoading] = useState(() => !getCachedProcedureCatalog());
  const boxRef = useRef(null);

  useEffect(() => {
    if (getCachedProcedureCatalog()) {
      // Cache already primed by an earlier mount; skip the fetch entirely.
      return;
    }
    fetchProcedureCatalog().then((rows) => {
      setCatalog(rows || []);
      setLoading(false);
    });
  }, []);

  useEffect(() => {
    const onDoc = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  // Case-insensitive substring match on name OR code. Recomputed on every
  // (query, catalog) change; O(N) but at ~11.5k rows this stays under 5 ms
  // even on mid-range mobile.
  const results = useMemo(() => {
    const q = (query || "").trim().toLowerCase();
    if (!q) return [];
    const out = [];
    for (const r of catalog) {
      const name = (r.name || "").toLowerCase();
      const code = (r.code || "").toLowerCase();
      if (name.includes(q) || code.includes(q)) {
        out.push(r);
        if (out.length >= MAX_RESULTS) break;
      }
    }
    return out;
  }, [query, catalog]);

  const handleType = (e) => {
    const q = e.target.value;
    setQuery(q);
    setOpen(q.trim().length > 0);
  };

  const pick = (item) => {
    onAdd({
      code: item.code || "",
      name: item.name || "",
      source: "catalog",
    });
    setQuery("");
    setOpen(false);
  };

  return (
    <div className="cc-icd" ref={boxRef}>
      <div className="sec-search">
        <svg
          className="sec-search-icon"
          viewBox="0 0 20 20"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.6"
          aria-hidden="true"
        >
          <circle cx="9" cy="9" r="6" />
          <path d="M14 14l4 4" strokeLinecap="round" />
        </svg>
        <input
          type="text"
          value={query}
          onChange={handleType}
          onFocus={() => query.trim() && setOpen(true)}
          placeholder={placeholder || "Search by CPT code or procedure name"}
          disabled={disabled}
        />
      </div>
      {/* Same dropdown sheet as the ICD search (2961:5051 family). Already-
          picked rows use the shared .li3-dd-opt.is-disabled treatment so a
          repeated pick is impossible without exiting the modal. */}
      {open && (
        <ul className="li3-dd" role="listbox">
          {loading && <li className="li3-dd-empty">Loading catalog…</li>}
          {!loading && results.length === 0 && (
            <li className="li3-dd-empty">No matches</li>
          )}
          {!loading &&
            results.map((r) => {
              const already = pickedSet ? pickedSet.has(identityKey(r)) : false;
              return (
                <li
                  key={r.code || r.name}
                  role="option"
                  aria-selected={false}
                  aria-disabled={already}
                  className={`li3-dd-opt icd-opt${already ? " is-disabled" : ""}`}
                  onClick={() => {
                    if (already) return;
                    pick(r);
                  }}
                  title={already ? "Already added to this hospitalisation" : undefined}
                >
                  <span className="icd-opt-name">{r.name}</span>
                  {r.code && <span className="hs3-cpt-tag">{r.code}</span>}
                </li>
              );
            })}
        </ul>
      )}
    </div>
  );
};

ProcedureCatalogAutocomplete.propTypes = {
  onAdd: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  placeholder: PropTypes.string,
  pickedSet: PropTypes.instanceOf(Set),
};

export default ProcedureCatalogAutocomplete;
